<template>
  <div class="min-h-screen bg-gray-50 px-4 py-6 pb-24" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-14 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div class="w-full">
            <h2 class="text-xl font-bold">گزارش ارجاعیات</h2>
            <p class="text-sm text-orange-50">بررسی و مدیریت درخواست‌های ارجاع شده</p>
          </div>
        </div>
      </div>

      <!-- Date Picker Section -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('fromDate') }}</label>
            <date-picker v-model="start_date" class="w-1/2"></date-picker>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('toDate') }}</label>
            <date-picker v-model="end_date" class="w-1/2"></date-picker>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="request_items.length === 0" class="flex flex-col items-center justify-center py-20">
        <div class="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p class="text-gray-500 text-sm">در بازه زمانی انتخاب شده درخواستی وجود ندارد</p>
      </div>

      <!-- Request Cards -->
      <div v-else class="space-y-4">
        <div 
          v-for="(item, i) in request_items"
          :key="i"
          class="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all duration-200"
        >
          <!-- Request Header -->
          <div class="mb-4 pb-4 border-b border-gray-200">
            <div class="flex items-center justify-between mb-2">
              <h3 class="w-full text-md font-bold text-gray-900">{{ item.RequestName }}</h3>
              <div 
                v-if="item.ImageStatus==0||item.ReceiveSignature==0 || item.SendSignature==0"
                @click="open_context_menu(item)"
                class="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center cursor-pointer transition-colors"
              >
                <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </div>
            </div>
            <p class="text-sm text-gray-500">{{ $t('requestNoQ') }}: <span class="font-bold text-gray-700">{{ item.RequestCode }}</span></p>
          </div>

          <!-- Request Details -->
          <div class="space-y-2 mb-4">
            <div class="flex items-center gap-2 text-sm text-gray-600">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{{ $t('referDateQ') }}: <span class="font-medium text-gray-900">{{ item.ReferDate }}</span></span>
            </div>
            
            <div v-if="get_request_address(item) != ''" class="flex items-start gap-2 text-sm text-gray-600">
              <svg class="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{{ get_request_address(item) }} {{ $t('addressQ') }}</span>
            </div>
          </div>

          <!-- Status Badge -->
          <div class="flex items-center justify-between">
            <div class="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              {{ item.LastStatus }}
            </div>
           
          </div>
        </div>
      </div>

    </div>

    <!-- Context Menu -->
    <vue-context ref="menu">
      <li>
        <a href="#" @click.prevent="context_menu_clicked('signature')">{{ $t('receiveSignature') }}</a>
      </li>
      <li>
        <a href="#" @click.prevent="context_menu_clicked('payments')">{{ $t('allPays') }}</a>
      </li>
      <li>
        <a href="#" @click.prevent="context_menu_clicked('attachment')">افزودن عکس/ویدئو</a>
      </li>
    </vue-context>

  </div>
</template>
<script>
  import Vue from 'vue'
  import moment from 'moment-jalaali'

  import VuePersianDatetimePicker from 'vue-persian-datetime-picker'
  import VueContext from 'vue-context'
  import 'vue-context/src/sass/vue-context.scss'

  Vue.component('date-picker', VuePersianDatetimePicker)
  export default {
    props:[
      'passing_data'
    ],
    data () {
      return {
        errors: [],
        start_date: '',
        end_date: '',
        request_items:[],
clicked_request_context:null,
      }
    },
components:{
      datePicker: VuePersianDatetimePicker,
      VueContext,
    },
    watch: {
      start_date: function () {
        // Save to sessionStorage
        if (this.start_date) {
          sessionStorage.setItem('report_start_date', this.start_date)
        }
        this.get_report(0)
      },
      end_date: function () {
        // Save to sessionStorage
        if (this.end_date) {
          sessionStorage.setItem('report_end_date', this.end_date)
        }
        this.get_report(0)
      },

    },
mounted () {
      // Load dates from sessionStorage or set default
      var saved_start_date = sessionStorage.getItem('report_start_date')
      var saved_end_date = sessionStorage.getItem('report_end_date')
      
      if (saved_start_date) {
        this.start_date = saved_start_date
      } else {
        this.start_date = moment().utc().format('jYYYY/jMM/jDD')
      }
      
      if (saved_end_date) {
        this.end_date = saved_end_date
      } else {
        this.end_date = moment().utc().format('jYYYY/jMM/jDD')
      }

      this.get_report(0)
    },
    methods: {
      open_context_menu (item) {
        var self = this
        var click_event = event
        this.clicked_request_context = item
        setTimeout(function () {
          self.$refs.menu.open(click_event, null)
          }, 150)
      },
      context_menu_clicked (func) {
        if (func == 'payments') {
          this.$router.push({ path:'/report-request/add-price-result/' + this.clicked_request_context.RecId })
        } else if (func == 'signature') {
          this.go_to_signature()
        } else if (func == 'attachment') {
          this.go_to_attachment()
        }
      },
      go_to_signature() {
        if (!this.clicked_request_context) return
        // Get ReqType from request, default to 5 if not available
        const req_type = this.clicked_request_context.ReqType || 5
        const rec_id = this.clicked_request_context.RecId || this.clicked_request_context.Id
        if (rec_id) {
          this.$router.push({ 
            name: 'signature', 
            params: { 
              rec_id: rec_id, 
              type_id: req_type 
            } 
          })
        }
      },
      go_to_attachment() {
        if (!this.clicked_request_context) return
        const rec_id = this.clicked_request_context.RecId || this.clicked_request_context.Id
        if (rec_id) {
          this.$router.push({ 
            name: 'attachment', 
            params: { 
              rec_id: rec_id
            } 
          })
        }
      },
      get_request_address (item) {
        var address = ''
        if (item.CityName != null) {
          address += item.CityName + ' '
        }
        if (item.AreaName != null) {
          address += item.AreaName + ' '
        }
        if (item.AddressText != null) {
          address += item.AddressText + ' '
        }
        return address
      },
      goToDetail (item_id) {
        this.$router.push({ name: 'groups', params: {} })
      },
      get_report (page) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId

        const send_data = {
            UserId: user_id,
            FirstDate: this.start_date,
            SecondDate: this.end_date,
            Page: page,

          }
          // this.axios.post('/Register' , send_data,{
          this.axios.post('/GetRequestRefersRptByUserId', send_data, {})
            .then(response => {
              if (response.data.error == 0) {
                // console.log("response", response);
                // self.user_registered = true;
                // self.fullName = response.data.msg;
                // self.level = 2;
                if (page == 0) {
                  self.request_items = response.data.refers
                } else {
                  response.data.refers.forEach(function (number) {
                   self.request_items.push(number)
                  })
                }
              } else {
                self.not_register = true
              }
              // this.$router.push({ path: "/post-login", params: {} });
            }).catch(e => {
            this.errors.push(e)
          })
      },

    }
  }
</script>
